import GameIllustration from './shared/GameIllustration';
// WelcomeFork — M1 in TAS-106.
//
// First screen of the tutorial flow. Routes the user into either a full
// 30-second practice tour ("Start walkthrough") OR straight to the M2 powers
// primer ("Skip to lobby"). Both routes still end at the unskippable primer,
// so nobody enters the game unaware of powers. (See PowersPrimer for M2;
// TutorialFlow.jsx wires the fork + the primer together.)
import { useEffect, useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { DUR, fx } from '../lib/anims';
import { track } from '@tasted/utils/analytics-logger';
import { NET_FULL } from '@tasted/utils/netTier';
import { useNetTier } from '../hooks/useNetTier';

export const FORK_CHOICE_KEY = (sessionCode) =>
  `tasted.tutorial.forkChoice.${sessionCode || 'unknown'}`;

export function readForkChoice(sessionCode) {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const v = window.localStorage.getItem(FORK_CHOICE_KEY(sessionCode));
    return v === 'new' || v === 'experienced' ? v : null;
  } catch {
    return null;
  }
}

export function writeForkChoice(sessionCode, choice) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try { window.localStorage.setItem(FORK_CHOICE_KEY(sessionCode), choice); }
  catch { /* private mode / quota — silent. */ }
}

// ─── Design tokens (local to this screen) ────────────────────────────────
const COLOR = {
  cream:     '#F9F6F1',
  ink:       '#1a1a1a',
  green:     '#1C3B29',
  greenDeep: '#152E20',
  greenSoft: '#A8C4A8',
  gold:      '#C9A84C',
  mute:      '#6a6258',
  border:    '#E5DED0',
  cardBg:    '#FFFFFF',
  eyebrow:   '#8A7F6A',
};
const FAMILY_SERIF = "'Fraunces', Georgia, serif";
const FAMILY_SANS  = "'Outfit', system-ui, sans-serif";

export default function WelcomeFork({ sessionCode, onChoice }) {
  // Subscribed, so a phone that measures slow while this screen is up swaps the
  // loop for the poster rather than waiting for some other render (Codex).
  const netTier = useNetTier();
  const mountedAt = useRef(Date.now());
  const heroRef      = useRef(null);
  const headingRef   = useRef(null);
  const primaryRef   = useRef(null);
  const secondaryRef = useRef(null);
  const footerRef    = useRef(null);

  useEffect(() => {
    track('Welcome Fork Shown', { sessionCode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Staggered entrance: hero → heading → primary → secondary → footer.
  // Uses fx.slideInUp so the durations/eases stay consistent with the rest
  // of the game (see lib/anims.js). Reduced-motion users see the elements
  // immediately with no transform, but still get the opacity fade so the
  // screen doesn't slam in without any settle.
  useLayoutEffect(() => {
    const els = [heroRef, headingRef, primaryRef, secondaryRef, footerRef]
      .map((r) => r.current)
      .filter(Boolean);
    if (!els.length) return;

    const reduced = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      gsap.set(els, { opacity: 0 });
      const tween = gsap.to(els, { opacity: 1, duration: DUR.fast, stagger: 0.03 });
      return () => tween.kill();
    }

    const tl = gsap.timeline();
    els.forEach((el, i) => {
      const step = fx.slideInUp(i);
      tl.fromTo(
        el,
        step.from,
        { ...step.to, duration: step.duration, ease: step.ease, clearProps: step.clearProps },
        i * 0.06,
      );
    });
    return () => tl.kill();
  }, []);

  const pick = (choice) => {
    const secondsOnScreen = Math.round((Date.now() - mountedAt.current) / 1000);
    writeForkChoice(sessionCode, choice);
    track('Welcome Fork Chosen', { sessionCode, choice, secondsOnScreen });
    onChoice?.(choice);
  };

  return (
    <div
      data-testid="welcome-fork"
      style={{
        position: 'relative',
        minHeight: '100dvh',
        background: COLOR.cream,
        color: COLOR.ink,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: FAMILY_SANS,
        padding: 'max(env(safe-area-inset-top, 0px), 24px) 20px 24px',
        gap: 20,
      }}
    >
      {/* ── Hero video — 20px radius, autoplay/loop/muted ────────────── */}
      <div
        ref={heroRef}
        style={{
          height: 168,
          borderRadius: 20,
          overflow: 'hidden',
          background: '#EDE8DE',
          boxShadow: '0 2px 12px rgba(28,59,41,0.06)',
        }}
      >
        {/* The poster is what this box shows whenever the video does not
            autoplay — and on a phone that is common: iOS refuses autoplay
            outright in Low Power Mode, and Data Saver blocks it too. Without
            one the hero was an empty beige rectangle at the top of the first
            screen a player ever sees. It is a frame lifted from the video, so
            the still and the first frame match. 17KB: the unreferenced
            hero-tasting.webp already in this folder is 1.5MB, heavier than the
            video, and a poster is fetched eagerly by every visitor. */}
        {/* Anything short of a full connection gets 17KB of poster instead of
            the 742KB loop. This is the first screen a player sees and the one
            where the tier is still being measured — spending the connection
            here delays the game itself (TAS-398). Full, not "not slow": a
            middle connection is one that cannot afford the 3D, and this is
            decoration. */}
        {netTier !== NET_FULL ? (
          <img
            src="/hero-tasting-poster.webp"
            alt=""
            aria-hidden
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
        <video
          src="/hero-tasting.mp4"
          poster="/hero-tasting-poster.webp"
          preload="metadata"
          autoPlay
          loop
          muted
          playsInline
          aria-hidden
          // React writes `muted` as a DOM property, not an HTML attribute, and
          // iOS Safari's autoplay policy reads the attribute. With no `muted`
          // attribute in the DOM the video counts as sound-on and Safari
          // refuses to autoplay — silently, in both Safari and Brave on iOS,
          // with the file, codec and server all correct. Setting it on the
          // element and asking for play() ourselves is the documented way
          // round it (facebook/react#10389).
          ref={(el) => {
            if (!el) return;
            el.muted = true;
            el.defaultMuted = true;
            el.setAttribute('muted', '');
            const p = el.play?.();
            if (p && typeof p.catch === 'function') p.catch(() => { /* poster stays */ });
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
        )}
      </div>

      {/* ── Heading block ────────────────────────────────────────────── */}
      <div ref={headingRef}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: COLOR.eyebrow,
            marginBottom: 8,
          }}
        >
          Game setup
        </div>
        <h1
          style={{
            fontFamily: FAMILY_SERIF,
            fontOpticalSizing: 'auto',
            fontWeight: 700,
            fontSize: 30,
            lineHeight: 1.06,
            margin: 0,
            color: COLOR.green,
          }}
        >
          Welcome to Tasted
        </h1>
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.5,
            color: COLOR.mute,
            margin: '8px 0 0',
            maxWidth: 320,
          }}
        >
          Start with a quick walkthrough, or jump straight into the lobby.
        </p>
      </div>

      {/* ── Route cards ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Primary — Start walkthrough */}
        <button
          ref={primaryRef}
          type="button"
          onClick={() => pick('new')}
          data-testid="fork-new"
          style={{
            textAlign: 'left',
            background: COLOR.green,
            color: '#F5F1E8',
            borderRadius: 18,
            border: 'none',
            padding: '18px 20px',
            cursor: 'pointer',
            fontFamily: FAMILY_SANS,
            boxShadow: '0 6px 18px rgba(28,59,41,0.18)',
            position: 'relative',
            minHeight: 88,
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 14,
              right: 16,
              background: COLOR.gold,
              color: COLOR.greenDeep,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              padding: '4px 10px',
              borderRadius: 999,
            }}
          >
            Recommended
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              paddingRight: 116, // leave room for badge
              lineHeight: 1.2,
            }}
          >
            Start walkthrough
          </div>
          <div
            style={{
              fontSize: 13,
              color: COLOR.greenSoft,
              marginTop: 6,
              lineHeight: 1.5,
              maxWidth: 280,
            }}
          >
            Learn how tasting, guessing and scoring work before the first pour.
          </div>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              marginTop: 10,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.05em',
              color: COLOR.greenSoft,
            }}
          >
            <span aria-hidden><GameIllustration name="timer" /></span>
            <span>Takes about 30 seconds</span>
          </div>
        </button>

        {/* Secondary — Skip to lobby */}
        <button
          ref={secondaryRef}
          type="button"
          onClick={() => pick('experienced')}
          data-testid="fork-experienced"
          style={{
            textAlign: 'left',
            background: COLOR.cardBg,
            color: COLOR.green,
            border: `1.5px solid ${COLOR.border}`,
            borderRadius: 18,
            padding: '16px 20px',
            cursor: 'pointer',
            fontFamily: FAMILY_SANS,
            minHeight: 76,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Skip to lobby</div>
            <div aria-hidden style={{ fontSize: 20, color: '#9a8f78' }}>→</div>
          </div>
          <div style={{ fontSize: 13, color: '#8a8270', marginTop: 4, lineHeight: 1.45 }}>
            I already know how to play.
          </div>
        </button>
      </div>

      {/* ── Reassurance footer ──────────────────────────────────────── */}
      <div
        ref={footerRef}
        style={{
          marginTop: 'auto',
          paddingTop: 12,
          textAlign: 'center',
        }}
      >
        <span style={{ fontSize: 12, color: '#a89e88', lineHeight: 1.5 }}>
          You can replay the walkthrough from the lobby at any time.
        </span>
      </div>
    </div>
  );
}
